// Routing utility using OSRM (Open Source Routing Machine)

export interface RouteInfo {
  duration: number; // Duration in seconds
  distance: number; // Distance in meters
  durationText: string; // Human-readable duration (e.g., "12 min")
  distanceText: string; // Human-readable distance (e.g., "3.2 mi")
  geometry?: any; // Route geometry for drawing on map (optional)
}

export interface RoutingResponse {
  routes: RouteInfo[];
  origin: {lat: number; lng: number};
  destination: {lat: number; lng: number};
}

/**
 * Calculate driving route between two points using OSRM
 */
export async function calculateRoute(
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  includeGeometry: boolean = false
): Promise<RouteInfo | null> {
  try {
    // Format coordinates for OSRM API: longitude,latitude
    const coordinates = `${originLng},${originLat};${destLng},${destLat}`;
    const geometryParam = includeGeometry ? '&geometries=geojson' : '';
    
    const response = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=false&alternatives=false&steps=false${geometryParam}`,
      {
        headers: {
          'User-Agent': 'Frisco-ISD-School-Zone-Map/1.0'
        }
      }
    );

    if (!response.ok) {
      console.warn(`OSRM routing error: ${response.statusText}`);
      return null;
    }

    const data = await response.json();
    
    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      console.warn('No route found:', data);
      return null;
    }

    const route = data.routes[0];
    
    return {
      duration: route.duration, // seconds
      distance: route.distance, // meters
      durationText: formatDuration(route.duration),
      distanceText: formatDistance(route.distance),
      geometry: includeGeometry ? route.geometry : undefined
    };
  } catch (error) {
    console.error('Routing error:', error);
    return null;
  }
}

/**
 * Calculate driving routes to multiple destinations
 */
export async function calculateMultipleRoutes(
  originLat: number,
  originLng: number,
  destinations: Array<{lat: number; lng: number; name: string}>
): Promise<Array<{name: string; route: RouteInfo | null}>> {
  // Calculate routes in parallel for better performance
  const routePromises = destinations.map(async (dest) => {
    const route = await calculateRoute(originLat, originLng, dest.lat, dest.lng);
    return {
      name: dest.name,
      route
    };
  });

  return Promise.all(routePromises);
}

/**
 * Format duration from seconds to human-readable text
 */
function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  
  return `${hours} hr ${remainingMinutes} min`;
}

/**
 * Format distance from meters to human-readable text
 */
function formatDistance(meters: number): string {
  const miles = meters * 0.000621371; // Convert meters to miles
  
  if (miles < 0.1) {
    const feet = meters * 3.28084;
    return `${Math.round(feet)} ft`;
  }
  
  if (miles < 10) {
    return `${miles.toFixed(1)} mi`;
  }
  
  return `${Math.round(miles)} mi`;
}

/**
 * Estimate driving time using straight-line distance (fallback when routing fails)
 */
export function estimateDrivingTime(
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number
): RouteInfo {
  // Calculate straight-line distance
  const distance = calculateStraightLineDistance(originLat, originLng, destLat, destLng);
  
  // Estimate driving distance as 1.3x straight-line distance (accounts for roads)
  const drivingDistance = distance * 1.3;
  
  // Estimate time assuming average speed of 25 mph in suburban areas
  const avgSpeedMph = 25;
  const drivingTimeHours = drivingDistance / avgSpeedMph;
  const drivingTimeSeconds = drivingTimeHours * 3600;
  
  return {
    duration: drivingTimeSeconds,
    distance: drivingDistance * 1609.34, // Convert miles to meters
    durationText: `~${formatDuration(drivingTimeSeconds)}`,
    distanceText: `~${formatDistance(drivingDistance * 1609.34)}`
  };
}

/**
 * Calculate straight-line distance between two points in miles
 */
function calculateStraightLineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return distance;
} 